import React, {
  ForwardRefRenderFunction,
  RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { LIMIT_PRODUCTS_INSIDE_CARTONS } from "../..";
import { useAppState } from "../../../../../context/StateContext";
import { Booking } from "../../../../../models/booking";
import { CartonProductView, CartonType, CartonView } from "../../../../../models/carton";
import { DocumentReferenceOptions } from "../../../../../models/document";
import { Product } from "../../../../../models/product";
import { PurchaseOrder, PurchaseOrderProduct } from "../../../../../models/purchase.order";
import { ShipmentDocument } from "../../../../../models/shipment.document";
import { ShipmentContainerInfo } from "../../../../../models/shipments";
import { getBase64 } from "../../../../../util/base64";
import { ExtraCheckKeys } from "../../../../../util/enums";
import env from "../../../../../util/env";
import readCSV, { ColumnMappingCSV } from "../../../../../util/readCSV";
import { NotificationToast } from "../../../Toast/NotificationToast";
import UploadCSVFile, { UploadCSVErrors } from "../../../UploadCSVFile";
import { ValidateCSVFieldErrors, addErrorInLine } from "../../../UploadCSVFile/types";
import * as ValidateCSV from "../../csv.validate";
import { filterCartonCsvHeaderMapping, getCartonCsvTemplateUrl, getCartonCsvValidationFlags } from "../../destination.csv.rules";
import { CartonTemplateCSV, generateRandomSSCC, getCartonCSVHeaderMapping } from "../../util";
import useAction from "../../hooks/useAction";

interface Props {
  ref: RefObject<UploadCSVFileHandles>;
  cartons: CartonView[];
  booking?: Booking;
  resources?: {
    purchaseOrders: PurchaseOrder[];
    products?: PurchaseOrderProduct[];
  };
  isLengthMandatory?: boolean;
  calledFromShipment?: boolean;
  showOriginCriterion?: boolean;

  updateCartons?: (cartons: CartonView[]) => void;
  onUploadCsvS3?: (csv: ShipmentDocument) => void;
  isToBrPort?: boolean;
}

export interface UploadCSVFileHandles {
  open: () => void;
  close: () => void;
}

const UploadCSVCartons: ForwardRefRenderFunction<UploadCSVFileHandles, Props> = (props, ref) => {
  const {
    updateCartons,
    cartons,
    booking,
    onUploadCsvS3,
    resources,
    isLengthMandatory,
    calledFromShipment,
    showOriginCriterion = false,
    isToBrPort = false,
  } = props;

  const { currentUser, isFMVersion } = useAppState();
  const { t: translate } = useTranslation();
  const { useGenerateMultipleSSCC } = useAction();

  const modalRef = useRef<UploadCSVFileHandles>(null);

  const [lineErrorsCSV, setLineErrorsCSV] = useState<UploadCSVErrors>({});
  const [generalErrorsCSV, setGeneralErrorsCSV] = useState<UploadCSVErrors>({});

  const { mutate: generateSSCCforCSV, isLoading: isGeneratingSSCCforCSV } =
    useGenerateMultipleSSCC();

  const openModal = useCallback(() => {
    modalRef.current?.open();
  }, []);
  const closeModal = useCallback(() => {
    modalRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const generalErrorMessage = useCallback(
    (firstElementReference: string, message: string) =>
      `${translate("carton")} ${firstElementReference} - ${message}`,
    []
  );

  const validateDuplicatedOrders = useCallback(
    (
      firstProduct: CartonProductView,
      poNumber: string,
      cartonReference: string,
      csvGlobalErrors: UploadCSVErrors
    ) => {
      if (firstProduct?.poNumber !== poNumber) {
        if (!csvGlobalErrors[cartonReference]) csvGlobalErrors[cartonReference] = [];

        csvGlobalErrors[cartonReference].push(
          generalErrorMessage(cartonReference, translate("haveProductsFromMultipleOrders"))
        );
      }
    },
    []
  );

  const validateDifferentColor = useCallback(
    (
      firstProduct: CartonProductView,
      product: Product,
      cartonReference: string,
      csvGlobalErrors: UploadCSVErrors
    ) => {
      if (firstProduct?.styleColor !== product?.styleColor) {
        if (!csvGlobalErrors[cartonReference]) csvGlobalErrors[cartonReference] = [];

        csvGlobalErrors[cartonReference].push(
          generalErrorMessage(
            cartonReference,
            translate("theCartonCanOnlyContainProductsOfTheSameTypeAndColor")
          )
        );
      }
    },
    []
  );

  const onConfirmUploadCSV = async (
    data: CartonTemplateCSV[],
    fileUploaded?: File,
    calledFromShipment: boolean = false,
    containers?: ShipmentContainerInfo[]
  ) => {
    if (!data || !data.length) {
      NotificationToast.warning(translate("thereAreNoRecordsInThisFile"), {
        id: "warning-file-empty",
      });
    }

    if (!data || !data.length || !resources || !resources.products || !resources.purchaseOrders)
      return;

    const { purchaseOrders: allOrders } = resources;

    const cartonsData: CartonView[] = [];
    const referencesToSSCC: { [x: string]: string } = {};
    const lineErrors: ValidateCSVFieldErrors = {};
    const csvGlobalErrors: UploadCSVErrors = {};

    const { shouldCheckUnitOfMeasure, showOriginCriterion: shouldCheckOriginCriterion } =
          getCartonCsvValidationFlags({ isToBrPort, showOriginCriterion });

    let { valid } = ValidateCSV.validateCSVFields(
      data,
      allOrders,
      lineErrors,
      translate,
      isLengthMandatory,
      booking
        ? [
            {
              label: ExtraCheckKeys.MODE,
              value: booking?.shipmentType || "",
            },
            {
              label: ExtraCheckKeys.ORIGIN_PORT,
              value: booking?.portOfLoadingId || 0,
            },
            {
              label: ExtraCheckKeys.DESTINATION_PORT,
              value: booking?.portOfDischargeId || 0,
            },
          ]
        : undefined,
      calledFromShipment,
      containers,
      shouldCheckUnitOfMeasure,
      shouldCheckOriginCriterion
    );

    if (valid) {
      data.forEach((dataItem, index) => {
        const line = index + 2;

        const purchaseOrderFound = allOrders.find(
          (order) => order?.reference === dataItem?.poNumber
        );

        const productsFromOrderFound = purchaseOrderFound?.purchaseOrderProducts;

        const purchaseOrderProductFound = productsFromOrderFound?.find(
          (orderProduct) => orderProduct?.product?.sku === dataItem?.sku
        );

        if (!purchaseOrderProductFound) return;

        const productFound = purchaseOrderProductFound?.product || [];

        referencesToSSCC[dataItem.cartonReference] =
          referencesToSSCC[dataItem.cartonReference] || generateRandomSSCC(20);

        const cartonDataFoundIndex = cartonsData.findIndex(
          (cartonData) => cartonData.sscc === referencesToSSCC[dataItem.cartonReference]
        );

        const dataItemQuantity = Number(dataItem.quantity || 0);

        if (cartonDataFoundIndex > -1) {
          const cartonItem = cartonsData[cartonDataFoundIndex] || undefined;

          const productsOfThisCarton = cartonItem?.products || [];

          const productWithOrderOfCartonFoundIndex = productsOfThisCarton.findIndex(
            (productOfThisCarton) =>
              productOfThisCarton.sku === productFound?.sku &&
              productOfThisCarton.poNumber === purchaseOrderFound?.reference
          );

          const [firstProduct] = productsOfThisCarton;

          validateDuplicatedOrders(
            firstProduct,
            dataItem?.poNumber,
            dataItem?.cartonReference,
            csvGlobalErrors
          );

          validateDifferentColor(
            firstProduct,
            productFound,
            dataItem?.cartonReference,
            csvGlobalErrors
          );

          if (
            productWithOrderOfCartonFoundIndex > -1 &&
            productsOfThisCarton[productWithOrderOfCartonFoundIndex]
          ) {
            const sumQuantity =
              Number(
                productsOfThisCarton[productWithOrderOfCartonFoundIndex].quantityInCarton || 0
              ) + dataItemQuantity;

            productsOfThisCarton[productWithOrderOfCartonFoundIndex] = {
              ...productsOfThisCarton[productWithOrderOfCartonFoundIndex],
              quantityInCarton: sumQuantity,
            };
          } else {
            productsOfThisCarton.push({
              ...productFound,
              description: purchaseOrderProductFound?.description,
              colorDescription: purchaseOrderProductFound?.colorDescription,
              itemDescription: purchaseOrderProductFound?.itemDescription,
              construction: dataItem.construction,
              lining: dataItem.lining,
              category: dataItem.category,
              fabric: dataItem.fabric,
              composition: dataItem.composition,
              manufactureDetails: dataItem.manufactureDetails,
              poNumber: purchaseOrderFound?.reference,
              quantityInCarton: dataItemQuantity,
              cubicVolume: Number(dataItem.cbm),
              cfsCbm: Number(dataItem.cbm),
              weight: Number(dataItem.weight),
              cfsWeight: Number(dataItem.weight),
              hsCode: dataItem.hsCode,
              hscode: dataItem.hsCode,
              gender: dataItem.gender,
              unitOfMeasure: isToBrPort ? dataItem.unitOfMeasure : undefined,
              length: isLengthMandatory && dataItem?.length ? dataItem.length : undefined,
              originCriterion: showOriginCriterion ? dataItem.originCriterion?.toUpperCase() : undefined,
            }) as CartonProductView;
          }

          if (productsOfThisCarton.length > LIMIT_PRODUCTS_INSIDE_CARTONS) {
            return addErrorInLine(
              lineErrors,
              line,
              ValidateCSV.messages(translate).maxProductsInCarton(
                dataItem.cartonReference,
                LIMIT_PRODUCTS_INSIDE_CARTONS
              ),
              () => (valid = false)
            );
          }

          cartonsData[cartonDataFoundIndex] = {
            ...cartonItem,
            type: productsOfThisCarton.length > 1 ? CartonType.MIXED : CartonType.SOLID,
            products: productsOfThisCarton,
            grossWeight: productsOfThisCarton.reduce(
              (prev, curr) => prev + Number(curr.weight || 0),
              0
            ),
            purchaseOrderId: purchaseOrderFound?.id,
            container: dataItem?.container ?? "",
          };
        } else {
          cartonsData.push({
            sscc: referencesToSSCC[dataItem.cartonReference],
            grossWeight: 0,
            type: CartonType.SOLID,
            purchaseOrderId: purchaseOrderFound?.id,
            container: dataItem?.container ?? "",
            products: [
              {
                ...productFound,
                description: purchaseOrderProductFound?.description,
                colorDescription: purchaseOrderProductFound?.colorDescription,
                itemDescription: purchaseOrderProductFound?.itemDescription,
                construction: dataItem.construction,
                lining: dataItem.lining,
                category: dataItem.category,
                fabric: dataItem.fabric,
                composition: dataItem.composition,
                manufactureDetails: dataItem.manufactureDetails,
                length: isLengthMandatory && dataItem?.length ? dataItem.length : undefined,
                poNumber: purchaseOrderFound?.reference,
                quantityInCarton: dataItemQuantity,
                cubicVolume: Number(dataItem.cbm),
                weight: Number(dataItem.weight),
                hsCode: dataItem.hsCode,
                hscode: dataItem.hsCode,
                cfsCbm: Number(dataItem.cbm),
                cfsWeight: Number(dataItem.weight),
                gender: dataItem.gender,
                unitOfMeasure: isToBrPort ? dataItem.unitOfMeasure : undefined,
                originCriterion: showOriginCriterion ? dataItem.originCriterion?.toUpperCase() : undefined,
              },
            ],
          });
        }
      });
    }

    if (!valid || !!Object.keys(csvGlobalErrors).length) {
      setLineErrorsCSV(lineErrors);
      setGeneralErrorsCSV(csvGlobalErrors);

      return;
    }
    const companyId = isFMVersion
      ? currentUser?.companyId
      : currentUser?.company?.supplierAssociated?.companyId;

    if (!companyId) return;

    const boxCount = new Set(data.map((item) => item.cartonReference)).size;

    generateSSCCforCSV(
      { sscc: { companyId, quantity: boxCount }, cartonsData },
      {
        onSuccess: (response, { cartonsData }) => {
          const cartonsDataWithGeneratedSSCC = cartonsData.map((carton, index) => {
            return { ...carton, sscc: response.data[index] };
          });

          updateCartons?.([...cartons, ...cartonsDataWithGeneratedSSCC]);
          modalRef.current?.close();
          NotificationToast.success(translate("operationPerformedSuccessfully"));
        },
      }
    );

    if (fileUploaded && onUploadCsvS3) {
      const uri = await getBase64<string>(fileUploaded, "dataURL");
      const shipmentDocumentToSave = {
        file: uri,
        uploadedAt: new Date().toISOString(),
        userId: currentUser?.id,
        documentId: undefined,
        name: DocumentReferenceOptions.SHIPMENT.SSCC_CSV_UPLOADED,
        fileType: "csv",
      } as ShipmentDocument;

      onUploadCsvS3(shipmentDocumentToSave);
    }
  };

  const csvFileColumns: ColumnMappingCSV<CartonTemplateCSV> = useMemo(() => {
    return filterCartonCsvHeaderMapping(getCartonCSVHeaderMapping(translate), {
      isToBrPort,
      showOriginCriterion,
    });
  }, [isToBrPort, showOriginCriterion, translate]);

  return (
    <UploadCSVFile
      ref={modalRef}
      onConfirmUpload={(uri, fileUploaded) => {
        readCSV<CartonTemplateCSV>(
          uri,
          csvFileColumns,
          (data) => onConfirmUploadCSV(data, fileUploaded, calledFromShipment, booking?.containers),
          {
            uuid: true,
          }
        );
      }}
      lineErrors={lineErrorsCSV}
      generalErrors={generalErrorsCSV}
      onCloseModal={() => {
        setLineErrorsCSV({});
        setGeneralErrorsCSV({});
      }}
      onDownloadTemplate={() => window
                .open(
                  getCartonCsvTemplateUrl(
                    { isToBrPort, showOriginCriterion },
                    {
                      defaultTemplate: env.LINK_TEMPLATE_UPLOAD_CARTONS_CSV || "",
                    }
                  ),
                  "_blank"
                )
                ?.focus()}
      loadingUpload={isGeneratingSSCCforCSV}
    />
  );
};

export default React.forwardRef(UploadCSVCartons);
